/**
 * V2 Avatar — gradient initials chip from design_reference (`.av`).
 * Picks a deterministic color from a palette based on the initials so the
 * same person always gets the same hue.
 */

import type { CSSProperties } from 'react';
import { cn } from '../../../lib/utils';

/** Gradient base colors (mirrors the prototype's avColors palette spirit). */
export const AVATAR_COLORS = [
  '#5b9bf6',
  '#a78bfa',
  '#2dd4bf',
  '#f5b13d',
  '#f87171',
  '#34d399',
  '#4f8cff',
] as const;

/**
 * Deterministically picks a palette color from a seed string.
 *
 * @param seed - The initials (or any stable id).
 * @returns A hex color from AVATAR_COLORS.
 */
function colorFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Derives up-to-two uppercase initials from a name.
 *
 * @param name - Full name or label.
 * @returns The initials (e.g. "Rich Milburn" -> "RM").
 */
export function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Props for the V2 Avatar. */
export interface AvatarProps {
  /** Pre-computed initials, OR pass `name` to derive them. */
  id?: string;
  /** Full name; used to derive initials + color when `id` is absent. */
  name?: string;
  /** Square edge length in px (default 24). */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/** V2 gradient-initials avatar. */
export function Avatar({ id, name, size = 24, className, style }: AvatarProps) {
  const label = id ?? initials(name ?? '');
  const c = colorFor(name ?? label);
  return (
    <span
      className={cn(
        'grid place-items-center rounded-full text-white font-[650] tracking-[.02em]',
        'border border-[rgba(255,255,255,.14)] shrink-0',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size * 0.44)),
        background: `linear-gradient(180deg, ${c}, color-mix(in oklab, ${c} 70%, black))`,
        ...style,
      }}
    >
      {label}
    </span>
  );
}
